'use client';

import { useBunja } from 'bunja/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Dialog } from '@flipster/react/design-system/dialog/index';
import { TextButton } from '@flipster/react/design-system/text-button';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { MarginBonusEvent } from '@flipster/utils/segment/events/margin-bonus';

import { leverageRestrictionPopupBunja } from './state';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const LeverageLimitRestrictPopup = (_props: Props) => {
  const overlay = useOverlay();

  const { shouldShowLeverageLimitRestrictPopupAtom } = useBunja(leverageRestrictionPopupBunja);
  const [shouldShowPopup, setShouldShowPopup] = useAtom(shouldShowLeverageLimitRestrictPopupAtom);

  const handleClose = useCallback(
    (close: () => void) => () => {
      close();
      setShouldShowPopup('close');
    },
    [setShouldShowPopup],
  );

  useEffect(() => {
    if (shouldShowPopup) {
      segment.track(MarginBonusEvent.PositionAdjustmentUnavailablePopupView);
      overlay.open(({ isOpen, close, unmount }) => (
        <Dialog.Root isOpen={isOpen} onExit={unmount}>
          <Dialog.Title
            label={<FormattedMessage id='Assets.r251112.Position_adjustment_unavailable' />}
            leftAddon={<CautionCircleIcon size={24} color={token('colors.content.state.warning')} />}
          />
          <Dialog.Content>
            <div>
              <FormattedMessage id='Assets.r251112.Position_adjustment_limited_due_to_bonus_policy' />
            </div>
            <TextButton
              as='a'
              onClick={handleClose(close)}
              href='/assets#assets-tabs'
              label={<FormattedMessage id='Common.r250401.Learn_more' />}
              color='passive'
              css={{ mt: 16 }}
            />
          </Dialog.Content>
          <Dialog.Footer>
            <Dialog.Button
              hierarchy='primary'
              label={<FormattedMessage id='Common.r250918.Got_it' />}
              onClick={handleClose(close)}
            />
          </Dialog.Footer>
        </Dialog.Root>
      ));
    }
  }, [shouldShowPopup, overlay, handleClose]);

  return null;
};
